/**
 * Pinia store for DeviceClass system
 * Provides cached access to available device classes and their metadata
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { deviceClassesApi } from '../services/api';
import { getRuntimeConfig } from '../config/runtime';
import { useWebRTCStore } from './webrtc';
import type { MappingRow } from '../shared/propertyMapping';

export interface DeviceClassMetadata {
  classId: string;
  name: string;
  icon: string;
  description: string;
  integrationTypes: string[];
  category: string;
  controlType?: string;
}

/**
 * One row of a device class's value/mapping/setting/behavior lists: the hub's
 * own JSON, keyed by `valueKey`/`settingKey` as the class editor reads it.
 */
export interface DeviceClassRow {
  valueKey?: string;
  settingKey?: string;
  [key: string]: unknown;
}

/** A value row of a class — the key every mapping of the class points at. */
export interface DeviceClassValue extends DeviceClassRow {
  valueKey: string;
  /** The value's display fields, which every class value carries. */
  name: string;
  valueType: string;
  unit?: string;
  grouping?: string;
  modeSettable?: boolean;
  modeCategory?: string | null;
  source?: string;
}

/** A settings row of a class — the key the class editor groups them by. */
export interface DeviceClassSetting extends DeviceClassRow {
  settingKey: string;
  label?: string;
  description?: string;
  settingType?: string;
  type?: string;
  /** `'drawing'` marks a variant picker (pump type, tank type, cover type…). */
  display?: string;
  /** The drawing family a `display: 'drawing'` setting picks (e.g. `'pump'`). */
  drawing?: string;
  sortOrder?: number;
  /** `false` on a presentation-only setting: changing it reloads no values. */
  affectsValues?: boolean;
  options?: { value: unknown; label?: string; icon?: string }[];
  step?: number | null;
  min?: number | null;
  max?: number | null;
  writableOnly?: boolean;
  visibleForIntegrations?: string[];
  visibleWhen?: { settingKey: string; operator: string; value: string | number | boolean | null };
}

/** The settings values a device class is instantiated with. */
export interface DeviceClassSettingsBag {
  [key: string]: unknown;
}

export interface DeviceClass extends DeviceClassMetadata {
  values: DeviceClassValue[];
  mappings: MappingRow[];
  /** The hub's own list; some classes still answer under the legacy key below. */
  settings?: DeviceClassSetting[];
  behaviors?: DeviceClassRow[];
  defaultSettings?: DeviceClassSettingsBag;
  /** Legacy key for `settings` — the editor still tolerates it (t_f4977822). */
  settingDefinitions?: DeviceClassSetting[];
  /** Legacy per-protocol mapping bags, read when a class ships no `mappings`. */
  integrationMappings?: { knx?: MappingRow[]; matter?: MappingRow[] };
}

export const useDeviceClassesStore = defineStore('deviceClasses', () => {
  // State
  const deviceClasses = ref<DeviceClassMetadata[]>([]);
  const deviceClassByIntegration = ref<Record<string, DeviceClassMetadata[]>>({});
  const deviceClassByCategory = ref<Record<string, DeviceClassMetadata[]>>({});
  const cachedFullClasses = ref<Map<string, DeviceClass>>(new Map());
  const loading = ref(false);
  const error = ref<string | null>(null);
  const initialized = ref(false);

  // Getters
  const allClasses = computed(() => deviceClasses.value);

  const classesGroupedByCategory = computed(() => {
    const grouped: Record<string, DeviceClassMetadata[]> = {};
    for (const deviceClass of deviceClasses.value) {
      const category = deviceClass.category || 'Other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(deviceClass);
    }
    return grouped;
  });

  /**
   * Get all device classes
   */
  async function initialize(): Promise<void> {
    if (initialized.value) {
      return; // Already initialized
    }

    // Skip initialization in cloud mode until connected to hub
    const config = getRuntimeConfig();
    if (config.mode === 'cloud') {
      const webrtcStore = useWebRTCStore();
      if (!webrtcStore.connected) {
        console.log('[DeviceClasses] Skipping initialization in cloud mode (not connected to hub)');
        return;
      }
    }

    try {
      loading.value = true;
      error.value = null;

      // Fetch all device classes
      const classes = await deviceClassesApi.getAll();
      deviceClasses.value = classes;

      // Group by integration type
      const byIntegration: Record<string, DeviceClassMetadata[]> = {};
      const byCategory: Record<string, DeviceClassMetadata[]> = {};

      for (const deviceClass of classes) {
        // Group by integration type
        for (const integrationType of deviceClass.integrationTypes) {
          if (!byIntegration[integrationType]) {
            byIntegration[integrationType] = [];
          }
          byIntegration[integrationType].push(deviceClass);
        }

        // Group by category
        const category = deviceClass.category || 'Other';
        if (!byCategory[category]) {
          byCategory[category] = [];
        }
        byCategory[category].push(deviceClass);
      }

      deviceClassByIntegration.value = byIntegration;
      deviceClassByCategory.value = byCategory;
      initialized.value = true;

      console.log(`[DeviceClasses] Initialized with ${classes.length} device classes`);
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load device classes';
      console.error('[DeviceClasses] Initialization failed:', err);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Get device classes by integration type.
   * Canonical unified classes declare all supported protocols in their
   * integrationTypes, so no cross-protocol aliasing is needed.
   */
  function getByIntegration(integrationType: string): DeviceClassMetadata[] {
    const normalized = integrationType.toLowerCase();
    return deviceClassByIntegration.value[normalized] || [];
  }

  /**
   * Get device classes by category
   */
  function getByCategory(category: string): DeviceClassMetadata[] {
    return deviceClassByCategory.value[category] || [];
  }

  /**
   * Get a specific device class by ID
   */
  function getById(classId: string): DeviceClassMetadata | undefined {
    return deviceClasses.value.find((c) => c.classId === classId);
  }

  /**
   * Get full device class details with settings and mappings
   */
  async function getFullClass(classId: string, settings?: DeviceClassSettingsBag): Promise<DeviceClass | null> {
    // Check cache first
    const cacheKey = settings ? `${classId}:${JSON.stringify(settings)}` : classId;
    if (cachedFullClasses.value.has(cacheKey)) {
      return cachedFullClasses.value.get(cacheKey) || null;
    }

    try {
      const fullClass = await deviceClassesApi.getById(classId, settings);
      cachedFullClasses.value.set(cacheKey, fullClass);
      return fullClass;
    } catch (err) {
      console.error(`[DeviceClasses] Failed to get full class ${classId}:`, err);
      return null;
    }
  }

  /**
   * Validate settings for a device class
   */
  async function validateSettings(
    classId: string,
    settings: DeviceClassSettingsBag,
  ): Promise<{ valid: boolean; errors?: string[] }> {
    try {
      const result = await deviceClassesApi.validateSettings(classId, settings);
      return result;
    } catch (err) {
      console.error(`[DeviceClasses] Failed to validate settings for ${classId}:`, err);
      return {
        valid: false,
        errors: [err instanceof Error ? err.message : 'Validation failed'],
      };
    }
  }

  /**
   * Clear cache - useful for refreshing data
   */
  function clearCache(): void {
    cachedFullClasses.value.clear();
  }

  /**
   * Reload all device classes
   */
  async function reload(): Promise<void> {
    initialized.value = false;
    clearCache();
    await initialize();
  }

  return {
    // State
    deviceClasses,
    deviceClassByIntegration,
    deviceClassByCategory,
    loading,
    error,
    initialized,

    // Getters
    allClasses,
    classesGroupedByCategory,

    // Methods
    initialize,
    getByIntegration,
    getByCategory,
    getById,
    getFullClass,
    validateSettings,
    clearCache,
    reload,
  };
});
